import type {
	HazmatClass,
	HazmatGroup,
} from "~/shared/schemas/hazmat-schema.ts";
import type { PostQuoteCommodity } from "~/shared/schemas/quote-schema.ts";
import type { HandlingUnit } from "~/shared/types/quote-details-types.ts";
import { idGenerator } from "~/shared/utils/id-generator.ts";

/**
 * Maps a PostQuoteCommodity from API response to HandlingUnit for UI
// The API commodity shape is adapted here until handling units and commodities converge.
*/

export const mapPostQuoteCommodityToHandlingUnit = (
	commodity: PostQuoteCommodity,
	hazmatClasses?: HazmatClass[],
	hazmatGroups?: HazmatGroup[],
): HandlingUnit => {
	const hazmatClassId =
		commodity.hazmatClassId ?? commodity.hazmatDetail?.hazmatClass;
	const packingGroupId =
		commodity.packingGroupId ?? commodity.hazmatDetail?.packingGroupId;
	const unNumberOrig = commodity.unNumber ?? commodity.hazmatDetail?.unNumber;
	const unNumber =
		unNumberOrig != null && unNumberOrig !== "" ? String(unNumberOrig) : null;

	const properShippingName =
		commodity.properShippingName ?? commodity.hazmatDetail?.properShippingName;
	const emergencyContactName =
		commodity.emergencyContactName ??
		commodity.hazmatDetail?.emergencyContactName;
	const emergencyContactPhone =
		commodity.emergencyContactPhone ??
		commodity.hazmatDetail?.emergencyContactPhone;

	const hazmatClass = hazmatClassId
		? (hazmatClasses?.find((c) => c.id === hazmatClassId) ?? {
				id: hazmatClassId,
				name: String(hazmatClassId),
				code: String(hazmatClassId),
			})
		: { id: 0, name: "select", code: "" };

	const packingGroupRaw = packingGroupId
		? (hazmatGroups?.find((g) => g.id === packingGroupId) ?? {
				id: packingGroupId,
				name: String(packingGroupId),
				code: String(packingGroupId),
			})
		: { id: 0, name: "select", code: "" };

	const packingGroup = {
		...packingGroupRaw,
		displayCode:
			packingGroupRaw.displayCode ??
			(packingGroupRaw.code === "None" ? "N/A" : packingGroupRaw.code),
	};

	return {
		id: commodity.id || idGenerator(),
		name: commodity.description ?? "",
		description: commodity.description ?? "",
		quantity: commodity.quantity,
		unit: {
			id: commodity.unitType.id,
			name: commodity.unitType.name,
			code: commodity.unitType.name, // Using name as code since API doesn't provide separate code field
		},
		weight: commodity.weight,
		density: 0, // Default - not provided by API
		dimensionLength: commodity.dimensionLength,
		dimensionWidth: commodity.dimensionWidth,
		dimensionHeight: commodity.dimensionHeight,
		class: {
			id: commodity.freightClass.id,
			name: commodity.freightClass.name,
			code: commodity.freightClass.name.replaceAll(/class/gi, ""), // Using name as code since API doesn't provide separate code field
		},
		nmfc: commodity.nmfc ?? "",
		isHazmat: commodity.isHazmat,
		isStackable: commodity.isStackable,
		pieceCount: commodity.pieceCaseCount,
		width: commodity.dimensionWidth,
		height: commodity.dimensionHeight,
		length: commodity.dimensionLength,
		estClass: 0, // Default - not provided by API
		unNumber: unNumber,
		packingGroupId: packingGroup,
		hazmatClass: hazmatClass,
		properShippingName: properShippingName ?? "",
		emergencyContactName: emergencyContactName ?? "",
		emergencyContactPhone: emergencyContactPhone ?? "",
	};
};

/**
 * Maps an array of PostQuoteCommodity to HandlingUnit array
 */
export const mapPostQuoteCommoditiesToHandlingUnits = (
	commodities: PostQuoteCommodity[],
	hazmatClasses?: HazmatClass[],
	hazmatGroups?: HazmatGroup[],
): HandlingUnit[] => {
	return commodities.map((commodity) =>
		mapPostQuoteCommodityToHandlingUnit(commodity, hazmatClasses, hazmatGroups),
	);
};
